import { LitElement } from "lit-element";
import { html } from "lit-html";
import { styleMap } from "lit-html/directives/style-map";
import { storeInstance } from "store/index.js";
import "@vaadin/vaadin-icons/vaadin-icons.js";
import "@ui5/webcomponents/dist/Button";
import "bds-tree";
import "./panel/checkbox-list";

class KbPropertyFilter extends LitElement {
  static get properties() {
    return {
      panelControlDefs: {
        type: Array
      },
      controlDefDataArray: {
        type: Array
      },
      panelControlDefsJSon: {
        type: Object,
      },
      propertyFilterState: {
        type: Array,
        state: true,
      },
      string: { type: String },
    };
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscription = storeInstance.subscribe((state) => {
      this.stateChange(state);
    });
    this.state = storeInstance.state;
    this.panelControlDefs = this.state.panel_control_defs;
    this.controlDefDataArray = this.state.control_def_data_array;
    this.panelControlDefsJSon = this.state.panel_control_defs_json;
    this.propertyFilterState = this.state.property_filter_state;
  }

  disconnectedCallback() {
    storeInstance.unsubscribe(this.subscription);
  }

  // Connects panel data to global store
  stateChange(state) {
    this.panelControlDefs = [...state.panel_control_defs];
    this.controlDefDataArray = [...state.control_def_data_array];
    this.panelControlDefsJSon = { ...state.panel_control_defs_json };
    this.propertyFilterState = [...state.property_filter_state];
    this.string = state.strings;
  }

  // <------------------ Helpers ----------------->

  // Finds index of control in panelControlDefsJSon
  _findControlDefIndex(controlDefId) {
    return this.panelControlDefsJSon.PanelControlValues.lstValues.findIndex(
      (def) => def.controlDefId === controlDefId
    );
  }

  // Finds index of control in propertyFilterState
  _findControlStateIndex(controlDefId) {
    return this.propertyFilterState.findIndex(
      (property) => property.id === controlDefId
    );
  }

  // <------------------ Updates ----------------->

  // update propertyFilterState
  _updateFilterState(controlDefId, type, data) {
    let hasData = Array.isArray(data)
      ? data.length > 0
      : Object.keys(data).length > 0;
    let index = this._findControlStateIndex(controlDefId);
    if (index >= 0) {
      if (hasData) {
        this.propertyFilterState[index]["data"] = { ...data };
      } else {
        this.propertyFilterState.splice(index, 1);
      }
    } else {
      if (hasData) {
        this.propertyFilterState.push({
          id: controlDefId,
          type: type,
          data: data,
        });
      }
    }
    storeInstance.dispatch(
      "updatePropertyFilterState",
      this.propertyFilterState
    );
  }

  // Finds index of control in panelDefJSon
  _updateJSon(controlDefId, data) {
    let hasData = data.length > 0;
    let index = this._findControlDefIndex(controlDefId);
    if (index >= 0) {
      if (hasData) {
        this.panelControlDefsJSon["PanelControlValues"]["lstValues"][index][
          "lstValues"
        ] = [...data]; //overwrite
      } else {
        this.panelControlDefsJSon["PanelControlValues"]["lstValues"].splice(
          index,
          1
        ); //remove
      }
    } else {
      if (hasData) {
        this.panelControlDefsJSon["PanelControlValues"]["lstValues"].push({
          controlDefId: controlDefId,
          lstValues: [...data],
        });
      }
    }
    storeInstance.dispatch("updateJSon", this.panelControlDefsJSon);
  }

  // Updates panelControlDefsJSon when user interacts with control
  _handleFilterChange(e) {
    let controlDefId = Number(e.target.dataset.controldefid);
    let controlDefType = e.target.dataset.controldeftype || "";
    let data = [];
    let treeState = {};
    if (controlDefType === "uiControlDefTreeDto") {
      treeState =
        e.detail.selectedKeys.length > 0
          ? { selected: e.detail.selectedKeys, expanded: e.detail.expandedKeys }
          : {};
      data = e.detail.selectedKeys.map((key) => ({ id: key, value: key }));
    } else {
      data = e.detail.data;
    }
    this._updateJSon(controlDefId, data);
    this._updateFilterState(controlDefId, controlDefType, treeState);
  }

  // <------------------ Data Getters ----------------->

  // Gets Data from panelControlDefsJSon
  _getControlState(def) {
    let index = this._findControlDefIndex(def.id);
    if (index >= 0) {
      return this.panelControlDefsJSon["PanelControlValues"]["lstValues"][
        index
      ]["lstValues"];
    }
    return [];
  }

  // <------------------ Templates ----------------->

  //Renders component based on control type in panel control def
  _appendControl(controlDef) {
    let index = this.controlDefDataArray.findIndex(
      (controlData) => controlData.controlDefId === controlDef.id
    );
    if (this.controlDefDataArray[index]) {
      let controlData = this.controlDefDataArray[index];
      switch (controlDef.type) {
        case "uiControlDefListDto":
          return html`<checkbox-list
            data-controlDefId=${controlDef.id}
            @listChanged=${this._handleFilterChange}
            .selected=${this._getControlState(controlDef)}
            .boxes=${controlData.propertyData.propertyValues}
          >
          </checkbox-list>`;

        case "uiControlDefTreeDto":
          return html`<bds-tree
            data-controlDefId=${controlDef.id}
            data-controlDefType=${controlDef.type}
            @treeChange=${this._handleFilterChange}
            .nodes=${controlData.propertyData.rootNode.children}
            labelPath="propertyValue.value"
            idPath="propertyValue.id"
            dataPath="propertyValue"
          >
          </bds-tree>`;
      }
    }
  }

  //Renders component based on control type in panel control def
  _appendControlList() {
    if (this.panelControlDefs.length > 1) {
      return this.panelControlDefs.map(
        (def) =>
          html`<div style="margin: 0 1rem 1rem 0; max-width: 15rem;">
            <h3 style="margin:0.5rem 0 0.5rem 0.5rem;">${def.label}</h3>
            ${this._appendControl(def)}
          </div> `
      );
    }
  }

  render() {
    const style = {
      background: "#cfd9e3",
      padding: "1rem 0 0 1rem",
      height: "100%",
      left: 0,
      right: 0,
      bottom: 0,
    };
    return html` <section style=${styleMap(style)}>
      <ul style="display: flex; flex-direction: column; padding: 0;">
        ${this._appendControlList()}
      </ul>
    </section>`;
  }
}

customElements.define("kb-property-filter", KbPropertyFilter);
